import 'rc-tooltip/assets/bootstrap.css';

import { XMarkIcon } from '@heroicons/react/24/outline';
import Tooltip from 'rc-tooltip';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { between, down, only, up } from 'styled-breakpoints';
import styled from 'styled-components';

import { WhiteButton } from '@rebound/design-system/components/Button';
import { colorSchemeConfig } from '@rebound/design-system/theme/colorScheme';
import { ThemeColorScheme } from '@rebound/shared-models/retailer/retailer.model';
import {
  Return,
  ReturnLabelType,
} from '@rebound/shared-models/return/return.model';

import ModalWrapper from '@components/ModalWrapper';
import { downloadBase64File } from '@modules/return-journey/return-journey.utils';

type ReturnLabelButtonProps = {
  returnObject: Return;
  scheme?: string;
};

const ReturnLabelButton = ({
  returnObject,
  scheme = ThemeColorScheme.Indigo,
}: ReturnLabelButtonProps) => {
  const [labelModalVisible, setLabelModalVisible] = useState(false);
  const { t } = useTranslation();

  const downloadLabelButtonDisabled = returnObject.label === '';

  useEffect(() => {
    if (labelModalVisible) {
      const closeOnEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setLabelModalVisible(false);
        }
      };
      window.addEventListener('keydown', closeOnEsc);

      return () => {
        window.removeEventListener('keydown', closeOnEsc);
      };
    }
  }, [labelModalVisible]);

  return (
    <Tooltip
      placement="bottom"
      trigger={downloadLabelButtonDisabled ? ['hover'] : []}
      overlay={t('returnsListing.downloadReturnLabelButtonTooltip')}
      overlayInnerStyle={{ margin: 0, padding: '15px 10px 0' }}
    >
      <Wrapper disable={downloadLabelButtonDisabled} scheme={scheme}>
        {returnObject.labelType === ReturnLabelType.PDF ? (
          <LinkWrapper
            onClick={() => {
              downloadBase64File(
                returnObject.label,
                returnObject.labelType,
                `Return_${returnObject.id}.pdf`,
              );
            }}
          >
            {t('returnsListing.downloadReturnLabelButton')}
          </LinkWrapper>
        ) : returnObject.labelType === ReturnLabelType.Image ? (
          <ShowCodeButton onClick={() => setLabelModalVisible(true)}>
            {t('returnsListing.showCode') as string}
          </ShowCodeButton>
        ) : null}

        {labelModalVisible && (
          <ModalWrapper>
            <Modal>
              <ContentWrapper>
                <Title>
                  {t('returnsListing.returnCodeReadyTitle') as string}
                </Title>
                <Description>
                  {
                    t('returnsListing.returnCodeReadyDescription', {
                      carrier: returnObject.carrierName || returnObject.carrier,
                    }) as string
                  }
                </Description>

                <ImageWrapper>
                  <img
                    src={`data:${returnObject.labelType};base64,${returnObject.label}`}
                    alt={`${returnObject.carrier} barcode or QR code`}
                  />
                  <TrackingNumber>{returnObject.tracking}</TrackingNumber>
                </ImageWrapper>

                <DownloadCodeModalButton
                  href={`data:${returnObject.labelType};base64,${returnObject.label}`}
                  download={`Return_${returnObject.tracking}`}
                >
                  {t('returnsListing.downloadReturnCode') as string}
                </DownloadCodeModalButton>

                <CloseIcon onClick={() => setLabelModalVisible(false)}>
                  <XMarkIcon height="20" />
                </CloseIcon>
              </ContentWrapper>
            </Modal>
          </ModalWrapper>
        )}
      </Wrapper>
    </Tooltip>
  );
};

const Wrapper = styled(WhiteButton)<{ scheme: string; disable: boolean }>`
  background-color: ${(props) => colorSchemeConfig[props.scheme].v700};
  color: ${(props) => props.theme.colors.white};

  div {
    ${({ disable, theme }) =>
      disable &&
      `
      pointer-events: none;
      color: ${theme.colors.grayLight4};
      text-decoration: none;
      cursor: default;
    `}
  }

  &:hover {
    opacity: 1;
    ${({ disable }) => disable && 'cursor: default'}
  }

  &:active {
    opacity: 1;
  }
`;

const LinkWrapper = styled.div`
  padding: 8px 0;
  color: ${(props) => props.theme.colors.white};

  ${only('xs')} {
    padding: 12px 0;
  }

  ${between('sm', 'md')} {
    padding: 12px 0;
  }
`;

const ShowCodeButton = styled.div`
  color: ${(props) => props.theme.colors.white};
  transition: 0.1s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }

  ${only('xs')} {
    padding: 14px 0;
  }

  ${between('sm', 'md')} {
    padding: 13px 12px;
  }

  ${up('md')} {
    padding: 10px 36px;
  }
`;

const Modal = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 25;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: auto;

  ${down('xs')} {
    padding: 0 8px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  padding: 24px 8px;
  margin: 0 8px;
  background-color: ${(props) => props.theme.colors.white};
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;

  ${down('xs')} {
    box-shadow:
      0px 20px 25px -5px rgba(0, 0, 0, 0.1),
      0px 10px 10px -5px rgba(0, 0, 0, 0.04);
    border-radius: 8px;
  }

  ${up('lg')} {
    margin: 0 140px;
  }
`;

const Title = styled.p`
  font-weight: 800;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  color: ${(props) => props.theme.colors.grayDark4};
  margin-bottom: 8px;
`;

const Description = styled.p`
  font-size: 14px;
  line-height: 20px;
  color: ${(props) => props.theme.colors.grayDark};
  text-align: center;
  max-width: 280px;
  margin-bottom: 20px;

  ${up('sm')} {
    max-width: 470px;
    margin-bottom: 24px;
  }
`;

const ImageWrapper = styled.div`
  width: 250px;
  height: auto;

  img {
    max-width: 100%;
    width: 100%;
  }
`;

const TrackingNumber = styled.p`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${(props) => props.theme.colors.black};
  text-align: center;
`;

const CloseIcon = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  margin: 20px 8px;
  cursor: pointer;

  ${up('sm')} {
    margin: 24px;
  }
`;

const DownloadCodeModalButton = styled.a`
  border-radius: 6px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  color: ${(props) => props.theme.colors.grayDark3};
  cursor: pointer;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  padding: 10px 8px;
  border: 1px solid ${(props) => props.theme.colors.grayDark2};
  width: 100%;
  margin-top: 34px;
  transition: 0.1s ease;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }

  ${only('xs')} {
    display: flex;
    justify-content: center;
  }

  ${up('sm')} {
    text-align: center;
    width: 374px;
    margin-top: 38px;
  }
`;

export default ReturnLabelButton;
